<template>
  <div>
    <small class="text-muted d-block mb-1">ไม่เลือก = ใช้ได้กับผู้ป่วยทุกคน</small>
    <b-input-group :size="small ? 'sm' : undefined">
      <multiselect
        class="col p-0"
        :class="{ 'multiselect--small': small }"
        v-model="selectedGroupObjects"
        :options="groupOptions"
        :multiple="true"
        :close-on-select="false"
        placeholder="ค้นหากลุ่มผู้ป่วย"
        label="text"
        track-by="value"
        selectLabel=""
        selectedLabel=""
        deselectLabel=""
        @input="onSelectionChange"
      >
        <span slot="noResult">ไม่พบกลุ่มผู้ป่วย</span>
      </multiselect>
      <b-input-group-append>
        <b-button :size="small ? 'sm' : undefined" variant="outline-secondary" @click="showBrowseModal">
          <i class="fas fa-list"></i>
        </b-button>
      </b-input-group-append>
    </b-input-group>

    <!-- Browse Modal -->
    <b-modal
      ref="browseModal"
      title="เลือกกลุ่มผู้ป่วย"
      ok-title="ตกลง"
      cancel-title="ยกเลิก"
      @ok="applyBrowseSelection"
      @show="initBrowseSelection"
      modal-class="picker-modal"
    >
      <div class="picker-search mb-2">
        <i class="fas fa-search picker-search-icon"></i>
        <b-form-input
          v-model="browseFilter"
          type="search"
          placeholder="ค้นหากลุ่มผู้ป่วย..."
          class="picker-search-input"
          autofocus
        />
      </div>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <b-badge variant="primary" pill class="px-2 py-1">
            <i class="fas fa-check mr-1"></i>{{ browseSelected.length }}
          </b-badge>
          <small class="text-muted ml-1">/ {{ filteredGroupOptions.length }} กลุ่ม</small>
        </div>
        <div>
          <b-button size="sm" variant="outline-primary" @click="selectAll" class="mr-1 btn-picker">
            <i class="fas fa-check-double mr-1"></i>เลือกทั้งหมด
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="deselectAll" class="btn-picker">
            <i class="fas fa-times mr-1"></i>ล้าง
          </b-button>
        </div>
      </div>
      <div class="picker-list-container">
        <div v-if="filteredGroupOptions.length === 0" class="text-center py-4 text-muted">
          <i class="fas fa-users-slash fa-2x mb-2 d-block"></i>
          <span>ไม่พบกลุ่มผู้ป่วย</span>
        </div>
        <div v-else class="picker-category-section">
          <div class="picker-category-header">
            <i class="fas fa-users mr-1"></i> กลุ่มผู้ป่วยทั้งหมด
            <b-badge variant="light" pill class="ml-1">{{ filteredGroupOptions.length }}</b-badge>
          </div>
          <div class="picker-items">
            <b-form-checkbox
              v-for="group in filteredGroupOptions"
              :key="group.value"
              v-model="browseSelected"
              :value="group.value"
              class="picker-item"
            >
              <i class="fas fa-user-friends text-muted mr-1" style="font-size: 0.75rem"></i>
              {{ group.text }}
              <small v-if="group.memberCount" class="text-muted ml-1">({{ group.memberCount }} คน)</small>
            </b-form-checkbox>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Multiselect from "vue-multiselect";

export default {
  name: "PatientGroupPicker",
  components: { Multiselect },
  props: {
    value: { type: Array, default: null },
    clinicUrl: { type: String, required: true },
    branchUrl: { type: String, required: true },
    small: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedGroupObjects: [],
      browseFilter: "",
      browseSelected: [],
    };
  },
  computed: {
    ...mapGetters("modulePatientGroup", ["getGroupList"]),
    groupOptions() {
      return (this.getGroupList || []).map(g => ({
        text: g.name,
        value: g.id,
        memberCount: g.memberCount || 0,
      }));
    },
    filteredGroupOptions() {
      if (!this.browseFilter) return this.groupOptions;
      const q = this.browseFilter.toLowerCase();
      return this.groupOptions.filter(g =>
        g.text.toLowerCase().includes(q)
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.syncFromValue(val);
      },
    },
    groupOptions() {
      this.syncFromValue(this.value);
    },
  },
  mounted() {
    this.fetchGroupList({
      clinicUrl: this.clinicUrl,
      branchUrl: this.branchUrl,
    });
  },
  methods: {
    ...mapActions("modulePatientGroup", ["fetchGroupList"]),
    syncFromValue(val) {
      if (!val || val.length === 0) {
        this.selectedGroupObjects = [];
        return;
      }
      this.selectedGroupObjects = this.groupOptions.filter(g =>
        val.includes(g.value)
      );
    },
    onSelectionChange(newVal) {
      const ids = (newVal || []).map(g => g.value);
      this.$emit("input", ids.length > 0 ? ids : null);
    },
    showBrowseModal() {
      this.$refs.browseModal.show();
    },
    initBrowseSelection() {
      this.browseFilter = "";
      this.browseSelected = this.value || [];
    },
    applyBrowseSelection() {
      const ids = this.browseSelected;
      this.$emit("input", ids.length > 0 ? ids : null);
      this.selectedGroupObjects = this.groupOptions.filter(g =>
        ids.includes(g.value)
      );
    },
    selectAll() {
      this.browseSelected = this.filteredGroupOptions.map(g => g.value);
    },
    deselectAll() {
      this.browseSelected = [];
    },
  },
};
</script>

<style scoped>
.picker-search {
  position: relative;
}
.picker-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.85rem;
  z-index: 1;
}
.picker-search-input {
  padding-left: 2.2rem;
  border-radius: 8px;
}
.btn-picker {
  border-radius: 6px;
  font-size: 0.78rem;
  padding: 0.25rem 0.6rem;
}
.picker-list-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
}
.picker-category-header {
  background: linear-gradient(135deg, #e8f4fd, #dbeafe);
  color: #1e60a0;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
}
.picker-items {
  padding: 0.35rem 0.35rem 0.35rem 0;
}
.picker-item {
  padding: 0.35rem 0.75rem;
  padding-left: 2rem;
  margin: 0;
  transition: background 0.15s;
  font-size: 0.88rem;
}
.picker-item:hover {
  background: #eef4fb;
}
.picker-item >>> .custom-control-label::before,
.picker-item >>> .custom-control-label::after {
  left: -1.4rem;
}
.multiselect--small >>> .multiselect__tags {
  min-height: 31px;
  padding: 4px 40px 0 8px;
  font-size: 0.875rem;
}
.multiselect--small >>> .multiselect__select {
  height: 31px;
}
.multiselect--small >>> .multiselect__tag {
  padding: 2px 22px 2px 6px;
  margin-bottom: 2px;
  font-size: 0.8rem;
}
.multiselect--small >>> .multiselect__tag-icon {
  width: 18px;
  line-height: 18px;
}
.multiselect--small >>> .multiselect__placeholder {
  margin-bottom: 2px;
  padding-top: 0;
  font-size: 0.875rem;
}
.multiselect--small >>> .multiselect__input {
  font-size: 0.875rem;
}
</style>
